import type { TooltipProps } from "@circleco/compass/components/Tooltip";
import { useIsTouchDevice } from "@circleco/compass/hooks/useIsTouchDevice";
import { usePrefersReducedMotion } from "@circleco/compass/hooks/usePrefersReducedMotion";
import { useEffect, useRef, useState } from "react";

const ANNOUNCEMENT_DURATION_MS = 3000;

type IntroPhase = "dots" | "icon" | "announcing" | "idle";

type IntroTooltipProps = Pick<
  TooltipProps,
  "open" | "disabled" | "onOpenChange" | "onOpenChangeComplete"
>;

export interface UseCircleAiIntroAnimationOptions {
  readonly shouldPlayIntro: boolean;
  readonly onIntroShown?: () => void;
}

export const useCircleAiIntroAnimation = ({
  shouldPlayIntro,
  onIntroShown,
}: UseCircleAiIntroAnimationOptions) => {
  const isTouchDevice = useIsTouchDevice();
  const prefersReducedMotion = usePrefersReducedMotion();
  const startPhase: IntroPhase = prefersReducedMotion ? "announcing" : "dots";
  const [phase, setPhase] = useState<IntroPhase>(() =>
    shouldPlayIntro ? startPhase : "idle",
  );
  const [isTooltipOpen, setIsTooltipOpen] = useState(false);
  const onIntroShownRef = useRef(onIntroShown);
  onIntroShownRef.current = onIntroShown;

  const isIdle = phase === "idle";
  const isAnnouncing = phase === "announcing";
  const isIntroAnimating = phase === "dots" || phase === "icon";

  useEffect(() => {
    if (isIdle && shouldPlayIntro) setPhase(startPhase);
  }, [isIdle, shouldPlayIntro, startPhase]);

  useEffect(() => {
    if (!isAnnouncing) return;

    onIntroShownRef.current?.();
    setIsTooltipOpen(true);
    const timeoutId = window.setTimeout(() => {
      setIsTooltipOpen(false);
      // Compass disables tooltips on touch, so no close ever completes there.
      if (isTouchDevice) setPhase("idle");
    }, ANNOUNCEMENT_DURATION_MS);

    return () => window.clearTimeout(timeoutId);
  }, [isAnnouncing, isTouchDevice]);

  const tooltipProps: IntroTooltipProps = {
    open: isTooltipOpen,
    disabled: isIntroAnimating,
    onOpenChange: (open, eventDetails) => {
      const isDismissal =
        eventDetails.reason === "escape-key" ||
        eventDetails.reason === "outside-press";
      if (!isAnnouncing || isDismissal) setIsTooltipOpen(open);
    },
    onOpenChangeComplete: open => {
      if (!open && isAnnouncing) setPhase("idle");
    },
  };

  return {
    isDotsPhase: phase === "dots",
    isIntroAnimating,
    isAnnouncing,
    setPhase,
    tooltipProps,
  };
};
